import { Timestamp } from "firebase/firestore";
import { z } from "zod";

// A bulk command reports per-target outcomes rather than a single pass/fail:
// the command succeeded, and some of its targets may still not have been
// reachable. Optional because only BULK_DELETE_ATTENDEES writes it.
export const bulkDeleteResultSchema = z.object({
  requestedCount: z.number(),
  deletedCount: z.number(),
  skipped: z
    .array(
      z.object({
        eventId: z.string(),
        attendeeId: z.string(),
        reason: z.string(),
      }),
    )
    .default([]),
});
export type BulkDeleteResult = z.infer<typeof bulkDeleteResultSchema>;

// Every governance command type. useGovernanceCommand parses the command
// document it is watching through this schema and IGNORES a snapshot that fails
// to parse — so a type missing here does not surface as a validation error, it
// leaves the form on "Processing..." forever while the backend has already
// succeeded. Add the type here in the same change that adds the command.
//
// Exported so a test can assert the set rather than a hand-copied subset. The
// previous inline list silently omitted SET_EVENT_ADMIN, and its test claimed
// to cover "all command types" while checking four of five.
//
// RESYNC_ATTENDEES is deliberately absent: no rule accepts it and no handler
// routes it, so listing it here would be the opposite mistake — a type the
// schema admits that the backend will never complete. See D9.
export const GOVERNANCE_COMMAND_TYPES = [
  "SET_PLATFORM_ROLE",
  "SET_DOMAIN_ALLOWLIST",
  "CREATE_EVENT",
  "SET_EVENT_ADMIN",
  "DELETE_USER",
  "BULK_DELETE_ATTENDEES",
  "MINT_INVITE",
  "REVOKE_INVITE",
] as const;
export type GovernanceCommandType = (typeof GOVERNANCE_COMMAND_TYPES)[number];

export const governanceCommandSchema = z.object({
  type: z.enum(GOVERNANCE_COMMAND_TYPES),
  requestedBy: z.string(),
  requestedAt: z.any(),
  status: z.enum(["REQUESTED", "IN_PROGRESS", "SUCCEEDED", "FAILED"]),
  error: z.string().optional(),
  result: bulkDeleteResultSchema.optional(),
  payload: z.record(z.string(), z.unknown()).optional(),
  // D21 MINT_INVITE settles with the raw token and the invite id it chose:
  // the platform_invites document stores only the hash, so this outcome is
  // the one surface the token exists on, readable by its minters.
  token: z.string().optional(),
  inviteId: z.string().optional(),
  expiresAt: z.any().optional(),
});
export type GovernanceCommand = z.infer<typeof governanceCommandSchema>;

// Deliberately permissive. This is the only surface that can remove a broken
// attendee record, so a record with a missing name, a blank email or a status
// nobody expected must still appear — a stricter schema would make exactly the
// rows worth deleting invisible. `eventId` and `attendeeId` are required
// because without both the row cannot be addressed at all.
export const platformAttendeeSchema = z.object({
  eventId: z.string().min(1),
  attendeeId: z.string().min(1),
  name: z.string().optional(),
  email: z.string().optional(),
  company: z.string().optional(),
  role: z.string().optional(),
  status: z.string().optional(),
  uid: z.string().optional(),
});
export type PlatformAttendee = z.infer<typeof platformAttendeeSchema>;

export const domainAllowlistEntrySchema = z.object({
  domain: z.string(),
});
export type DomainAllowlistEntry = z.infer<typeof domainAllowlistEntrySchema>;

export const auditLogEntrySchema = z.object({
  id: z.string().min(1),
  at: z.instanceof(Timestamp),
  actorUid: z.string().min(1),
  action: z.string().min(1),
  targetRef: z.string().min(1),
  before: z.record(z.string(), z.unknown()).optional(),
  after: z.record(z.string(), z.unknown()).optional(),
});
export type AuditLogEntry = z.infer<typeof auditLogEntrySchema>;

// email and name are optional so an incompletely provisioned user still
// appears in the governance list. Dropping them would make the account
// impossible to administer, since this is the only role-assignment surface.
export const governanceUserSchema = z.object({
  uid: z.string(),
  email: z.string().optional(),
  name: z.string().optional(),
  // Shown on the Users tab and searched there. A malformed value is dropped
  // rather than failing the parse, because a failed parse removes the user
  // from the only surface that can change their platform role.
  company: z.string().optional().catch(undefined),
  jobTitle: z.string().optional().catch(undefined),
  platformRole: z.enum(["user", "admin", "super_admin"]).optional(),
  createdAt: z.any().optional(),
});
export type GovernanceUser = z.infer<typeof governanceUserSchema>;

// A document of `event_admin_states`, the SET_EVENT_ADMIN watermark. Only the
// two fields the Current event admins list reads; `uid` is required because a
// row that names nobody cannot be shown.
export const eventAdminStateSchema = z.object({
  uid: z.string().min(1),
  isEventAdmin: z.boolean(),
});
export type EventAdminState = z.infer<typeof eventAdminStateSchema>;

// Only what the Event Admins picker needs. Deliberately not the full event
// shape: this is the governance view of an event, not the admin view, and
// widening it would invite governance UI to start editing events out of band.
export const governanceEventSchema = z.object({
  eventId: z.string(),
  // Optional, and the hook substitutes the id. A required title would make an
  // untitled event fail safeParse and vanish from the only surface that can
  // give it an administrator — the same failure the hook avoids by not using
  // orderBy.
  title: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  published: z.boolean().optional(),
  // Written only by the archive command. Absent means false.
  // A wrong type reads as absent: this parse must not hide an event from the
  // only surface that can give it an administrator.
  archived: z.boolean().optional().catch(undefined),
  // Decides when the event is over; absent falls back to the reader's zone.
  timezone: z.string().optional().catch(undefined),
});
export type GovernanceEvent = z.infer<typeof governanceEventSchema>;
